import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";

import ImageUpload from "./components/ImageUpload/ImageUpload";
import Header from "./components/Header/Header";
import Sidebar from "./components/Sidebar/Sidebar";
import MobileSearchTab from "./components/Sidebar/MobileSearchTab";
import Home from "./components/Home/Home";
import Communities from "./components/Communities/Communities";
import CommunityPage from "./components/Communities/CommunityPage";
import Settings from "./components/Settings/Settings";
import Profile from "./components/Profile/Profile";
import ProfileRouter from "./components/Profile/ProfileRouter";

import ShowcasePage from "./components/Profile/Showcase/ShowcasePage";
import ShowcaseDetails from "./components/Profile/Showcase/ShowcaseDetails";
import EditShowcase from './components/Profile/Showcase/EditShowcase';

import Messages from "./components/Messages/Messages";
import Notifications from "./components/Notifications/Notifications";
import EditProfile from "./components/Profile/EditProfile";
import Auth from "./components/Auth/Auth";
import SecurityDashboard from "./components/Settings/Security/SecurityDashboard";
import SearchResults from "./components/SearchResults/SearchResults";
import CreateSearch from "./components/CreateListing/CreateSearch";
import Blocked from "./components/Settings/Blocked/Blocked";
import EditCommunity from "./components/Communities/EditCommunity";
import CreateListing from "./components/CreateListing/CreateListing";
import Archive from "./components/Settings/Archive/Archive";
import Saved from "./components/Saved/Saved";
import ListingDetails from "./components/CreateListing/ListingDetails";
import CategoryFeedPage from "./components/Feed/CategoryFeedPage";
import LocationFeedPage from "./components/Feed/LocationFeedPage";
import EditPost from "./components/Edit/EditPost";
import EditListing from "./components/Edit/EditListing";
import EditSearch from "./components/Edit/EditSearch";
import Subscription from "./components/Settings/Subscription/Subscription";
import PaymentReturn from "./pages/PaymentReturn";
import AllCategories from "./pages/AllCategories";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { WalletProvider } from "./contexts/WalletContext";
import { UnreadCountsProvider } from "./contexts/UnreadCountsContext";
import SupportCvanto from "./components/Settings/SupportCvanto/SupportCvanto";
import Terms from "./components/Settings/Terms/Terms";
import Privacy from "./components/Settings/Privacy/Privacy";
import About from "./components/Settings/About/About";
import DeleteAccount from "./components/Settings/Privacy/DeleteAccount";
import HelpCenter from "./components/Settings/HelpCenter/HelpCenter";
import AdminReports from "./components/Settings/AdminReports/AdminReports";
import AdsCabinet from "./components/Settings/AdsCabinet/AdsCabinet";
import PublicAdReport from "./components/Settings/AdsCabinet/PublicAdReport";
import Trust from "./components/Settings/Trust/Trust";
import Statistic from "./components/Settings/Statistic/Statistic";
import { pingServer } from "./api/ping";
import { sendWebSessionBeacon } from "./utils/webSessionBeacon";
import { startPresenceHeartbeat } from "./utils/presenceHeartbeat";
import Chat from "./components/Messages/Chat";
import PromotionDashboard from "./components/Settings/Promotion/PromotionDashboard";
import Boost from "./components/Settings/Promotion/Boost";
import Balance from "./components/Settings/Balance/Balance";
import ShoppingProfile from "./components/Settings/ShoppingProfile/ShoppingProfile";
import Bill from "./components/Settings/Bill/Bill";
import { signOut } from "./awsAuth";
import BusinessEditProfile from "./components/Profile/BusinessProfile/BusinessEditProfile";
import ProductsCatalogAll from "./components/Profile/BusinessProfile/modules/ProductsCatalogAll";
import ServicesCatalogAll from "./components/Profile/BusinessProfile/modules/ServicesCatalogAll";
import IosAppBanner from "./components/IosAppBanner/IosAppBanner";
import VacanciesAll from "./components/Profile/BusinessProfile/modules/VacanciesAll";
import BookingsAll from "./components/Profile/BusinessProfile/modules/BookingsAll";
import BusinessListingsAll from "./components/Profile/BusinessProfile/modules/BusinessListingsAll";
import BusinessPostsAll from "./components/Profile/BusinessProfile/modules/BusinessPostsAll";
import BeforeAfterAll from "./components/Profile/BusinessProfile/modules/BeforeAfterAll";

// deep-link по Headline
import HeadlinePage from "./components/Headline/HeadlinePage";

// Cvanto Search Phase 4B
import BuyerRequestDetail from "./components/BuyerRequest/BuyerRequestDetail";
import CvantoAIPage from "./components/CvantoAI/CvantoAIPage";
import MyRequests from "./components/Settings/MyRequests/MyRequests";

const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, loading } = useAuth();

  if (loading) {
    return null;
  }

  return isLoggedIn ? children : <Navigate to="/auth" replace />;
};

const App = () => {
  const { isLoggedIn, setIsLoggedIn } = useAuth();
  const location = useLocation();

  const hideLayout =
  location.pathname.startsWith("/auth") ||
  location.pathname.startsWith("/login") ||
  location.pathname.startsWith("/settings");
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  const logout = () => {
    // Fully invalidate Cognito session, not just local UI state.
    signOut(setIsLoggedIn);
  };

  useEffect(() => {
    pingServer().then((result) => {
      console.log("Сервер відповів:", result);
    });
  }, []);

  // Once per real page load (App mounts once; client-side route changes
  // never remount it), not once per route — see webSessionBeacon.js.
  useEffect(() => {
    sendWebSessionBeacon();
  }, []);

  // Presence heartbeat — see presenceHeartbeat.js for the interval/
  // visibility-gating logic itself; startPresenceHeartbeat() is a no-op on
  // every call after the first, so this being in a mount effect (not
  // conditioned on auth state) is safe even if App itself ever re-mounts.
  useEffect(() => {
    startPresenceHeartbeat();
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  return (
    <SubscriptionProvider>
    <WalletProvider>
      {!hideLayout && (
        <UnreadCountsProvider>
          <Header isLoggedIn={isLoggedIn} onLogout={logout} onThemeToggle={toggleTheme} />
          <Sidebar />
          <MobileSearchTab />
        </UnreadCountsProvider>
      )}

      <IosAppBanner />

      <div className="app__content">
        <Routes>
          {/* Завантаження зображень */}
          <Route path="/upload-image" element={<ImageUpload />} />

          {/* Головна */}
          <Route path="/" element={<Home />} />

          {/* Усі категорії — secondary entry point, винесено з Home (fashion-first redesign) */}
          <Route path="/categories" element={<AllCategories />} />

          {/* /feed як синонім Home */}
          <Route path="/feed" element={<Navigate to="/" replace />} />

          {/* Авторизація */}
          <Route path="/login" element={<Auth setIsLoggedIn={setIsLoggedIn} />} />
          <Route path="/auth" element={<Auth setIsLoggedIn={setIsLoggedIn} />} />

          {/* Профіль */}
          <Route path="/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfileRouter isOwner={true} />
              </ProtectedRoute>
            }
          />

          <Route path="/profile/:userId" element={<ProfileRouter />} />

          <Route
            path="/business/edit"
            element={
              <ProtectedRoute>
                <BusinessEditProfile />
              </ProtectedRoute>
            }
          />

          <Route
            path="/business/products"
            element={
              <ProtectedRoute>
                <ProductsCatalogAll />
              </ProtectedRoute>
            }
          />

          <Route path="/profile/:userId/products" element={<ProductsCatalogAll />} />

          <Route
            path="/business/services"
            element={
              <ProtectedRoute>
                <ServicesCatalogAll />
              </ProtectedRoute>
            }
          />

          <Route path="/profile/:userId/services" element={<ServicesCatalogAll />} />

          {/* ВІТРИНИ */}
          {/* Список/керування */}
          <Route path="/showcases" element={<ShowcasePage />} />
          <Route path="/profile/:userId/vacancies" element={<VacanciesAll />} />
          <Route path="/business/vacancies" element={<VacanciesAll />} />

          {/* Деталі/наповнення конкретної вітрини */}
          <Route path="/showcase/:id" element={<ShowcaseDetails />} />
          <Route path="/showcase/:id/edit" element={<ProtectedRoute><EditShowcase /></ProtectedRoute>} />

          {/* короткий публічний URL (незалежно від /profile) */}
          <Route path="/s/:id" element={<ShowcaseDetails />} />

          {/* Спільноти */}
          <Route path="/communities" element={<Communities />} />
          <Route path="/communities/:id" element={<CommunityPage />} />

          {/* Редагування/створення спільнот */}
          <Route path="/community/:id/edit" element={<ProtectedRoute><EditCommunity /></ProtectedRoute>} />

          {/* Налаштування */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings setIsLoggedIn={setIsLoggedIn} onThemeToggle={toggleTheme} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/subscription"
            element={
              <ProtectedRoute>
                <Subscription />
              </ProtectedRoute>
            }
          />

          {/* Повідомлення */}
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/messages/:threadId" element={<ProtectedRoute><Messages /></ProtectedRoute>} />

          {/* Окремий маршрут для швидкого відкриття діалогу */}
          <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />

          {/* Сповіщення */}
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />

          {/* Безпека */}
          <Route path="/settings/security" element={<SecurityDashboard />} />

          {/* Пошук */}
          <Route path="/search" element={<SearchResults />} />
          <Route path="/create-search" element={<ProtectedRoute><CreateSearch /></ProtectedRoute>} />

          {/* Заблоковані */}
          <Route path="/settings/blocked" element={<Blocked />} />

          {/* Додати оголошення */}
          <Route path="/add-ad" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />

          {/* Архів */}
          <Route path="/settings/archive" element={<Archive />} />

          {/* Збережені */}
          <Route path="/saved" element={<Saved />} />

          {/* Лістінги */}
          <Route path="/listing/:id" element={<ListingDetails />} />
          <Route path="/category/:slug" element={<CategoryFeedPage />} />
          {/* City / city+category / city+subcategory / category+subcategory
              landing pages (SEO location architecture, 2026-09) — the human
              counterpart to cvanto-ssr's bot-facing SSR pages at the same
              URLs, so a visitor landing here from a search result sees real
              content instead of falling through to the catch-all redirect. */}
          <Route path="/city/:citySlug" element={<LocationFeedPage />} />
          <Route path="/city/:citySlug/:nodeSlug" element={<LocationFeedPage />} />
          <Route path="/category/:slug/:subSlug" element={<LocationFeedPage />} />
          <Route path="/edit/post/:postId" element={<EditPost />} />
          <Route path="/edit/listing/:listingId" element={<EditListing />} />
          <Route path="/edit/search/:id" element={<EditSearch />} />

          {/* Платежі */}
          <Route path="/payments/return" element={<PaymentReturn />} />
          <Route path="/settings/billing" element={<Bill />} />

          {/* Підтримка/документи */}
          <Route path="/settings/support-cvanto" element={<SupportCvanto />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/about" element={<About />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/settings/terms" element={<Terms />} />
          <Route path="/settings/privacy" element={<Privacy />} />
          <Route path="/settings/help" element={<HelpCenter />} />
          <Route path="/settings/admin-reports" element={<AdminReports />} />
          <Route path="/settings/ads-cabinet" element={<AdsCabinet />} />
          <Route path="/ad-report/:token" element={<PublicAdReport />} />
          <Route path="/settings/trust" element={<Trust />} />

          <Route path="/business/bookings" element={<BookingsAll />} />

          <Route
            path="/profile/:userId/before-after"
            element={<BeforeAfterAll />}
          />

          <Route
            path="/profile/:userId/products"
            element={<ProductsCatalogAll />}
          />

          <Route
            path="/profile/:userId/listings"
            element={<BusinessListingsAll />}
          />

          <Route
            path="/business/listings"
            element={<BusinessListingsAll />}
          />

          <Route
            path="/profile/:userId/posts"
            element={<BusinessPostsAll />}
          />

          {/* Статистика */}
          <Route path="/settings/statistics" element={<Statistic />} />

          {/* Просування */}
          <Route path="/settings/balance" element={<ProtectedRoute><Balance /></ProtectedRoute>} />
          <Route path="/settings/shopping-profile" element={<ProtectedRoute><ShoppingProfile /></ProtectedRoute>} />
          <Route path="/settings/promotion" element={<PromotionDashboard />} />
          <Route path="/settings/promotion/boost" element={<ProtectedRoute><Boost /></ProtectedRoute>} />

          {/* deep-link для Headline */}
          <Route path="/headline/:headlineId" element={<HeadlinePage />} />

          {/* Cvanto Search Phase 4B — Buyer Requests */}
          <Route path="/request/:requestId" element={<ProtectedRoute><BuyerRequestDetail /></ProtectedRoute>} />
          <Route path="/ai" element={<ProtectedRoute><CvantoAIPage /></ProtectedRoute>} />
          <Route path="/ai/:conversationId" element={<ProtectedRoute><CvantoAIPage /></ProtectedRoute>} />
          <Route path="/settings/buyer-requests" element={<ProtectedRoute><MyRequests /></ProtectedRoute>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/feed" replace />} />
        </Routes>
      </div>
    </WalletProvider>
    </SubscriptionProvider>
  );
};

export default App;